/**
 * Canvas store (zustand) for the no-code agent builder.
 *
 * Holds the ReactFlow `nodes`/`edges` graph plus selection, and exposes the
 * mutations the editor needs: add a node from the palette, remove a node (and
 * its incident edges), connect two nodes, apply ReactFlow change/connect
 * callbacks, and select a node. It also (de)serializes between the ReactFlow
 * graph and the backend agent-definition wire shape (`AgentNode`/`AgentEdge`)
 * so the editor can load a saved agent and save its edits back via the agents
 * API.
 *
 * The store is intentionally framework-thin: ReactFlow owns rendering, this
 * store owns state. Node `data.type` carries the backend node-type constant so
 * the canvas node components and DAG serialization stay keyed to
 * `core.constants` (via `lib/agents.NODE_TYPES`).
 */

import { create } from 'zustand';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from 'reactflow';
import {
  NODE_TYPE_META,
  type AgentDefinition,
  type AgentEdge,
  type AgentNode,
  type NodeType,
} from '../lib/agents';

/** Per-node data carried on the ReactFlow node (drives the node component). */
export interface CanvasNodeData {
  /** Backend node-type constant (e.g. 'rag.query'). */
  type: NodeType;
  /** Human-readable node name (editable label). */
  name: string;
  /** Node parameters / static config persisted to the agent definition. */
  params: Record<string, unknown>;
}

export type CanvasNode = Node<CanvasNodeData>;
export type CanvasEdge = Edge;

export interface CanvasState {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  /** Currently selected node id (for the inspector / delete affordance). */
  selectedNodeId: string | null;
  /** Monotonic counter so generated node ids are unique within a session. */
  nodeSeq: number;

  // -- ReactFlow change handlers ------------------------------------------
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  // -- explicit mutations -------------------------------------------------
  /** Add a node of `type` at `position`; returns the new node id. */
  addNode: (type: NodeType, position: { x: number; y: number }) => string;
  /** Remove a node and any edges incident to it. */
  removeNode: (nodeId: string) => void;
  /** Connect two nodes by id; no-op on self-loops or duplicate edges. */
  connect: (sourceId: string, targetId: string) => void;
  /** Update a node's editable name. */
  renameNode: (nodeId: string, name: string) => void;
  /** Select a node (or clear selection with `null`). */
  selectNode: (nodeId: string | null) => void;

  // -- (de)serialization --------------------------------------------------
  /** Replace the graph from a loaded agent definition. */
  loadDefinition: (definition: AgentDefinition) => void;
  /** Serialize the current graph into wire `nodes`/`edges`. */
  toDefinition: () => { nodes: AgentNode[]; edges: AgentEdge[] };
  /** Reset to an empty canvas. */
  reset: () => void;
}

let edgeSeq = 0;
function newEdgeId(source: string, target: string): string {
  edgeSeq += 1;
  return `e-${source}-${target}-${edgeSeq}`;
}

/** Build a ReactFlow node from a backend node type + position. */
function makeNode(
  id: string,
  type: NodeType,
  position: { x: number; y: number },
  name?: string,
  params?: Record<string, unknown>,
): CanvasNode {
  return {
    id,
    // The ReactFlow node `type` IS the backend node-type constant, so the
    // matching typed component (TriggerNode/RagNode/…) renders this node.
    type,
    position,
    data: {
      type,
      name: name ?? NODE_TYPE_META[type].label,
      params: params ?? {},
    },
  };
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  nodes: [],
  edges: [],
  selectedNodeId: null,
  nodeSeq: 0,

  onNodesChange: (changes) => {
    set((state) => ({ nodes: applyNodeChanges(changes, state.nodes) }));
  },

  onEdgesChange: (changes) => {
    set((state) => ({ edges: applyEdgeChanges(changes, state.edges) }));
  },

  onConnect: (connection) => {
    if (!connection.source || !connection.target) return;
    if (connection.source === connection.target) return;
    set((state) => ({
      edges: addEdge(
        {
          ...connection,
          id: newEdgeId(connection.source as string, connection.target as string),
        },
        state.edges,
      ),
    }));
  },

  addNode: (type, position) => {
    const seq = get().nodeSeq + 1;
    const id = `node-${type.replace('.', '-')}-${seq}`;
    const node = makeNode(id, type, position);
    set((state) => ({
      nodes: [...state.nodes, node],
      nodeSeq: seq,
      selectedNodeId: id,
    }));
    return id;
  },

  removeNode: (nodeId) => {
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== nodeId),
      edges: state.edges.filter(
        (e) => e.source !== nodeId && e.target !== nodeId,
      ),
      selectedNodeId:
        state.selectedNodeId === nodeId ? null : state.selectedNodeId,
    }));
  },

  connect: (sourceId, targetId) => {
    if (sourceId === targetId) return;
    set((state) => {
      const exists = state.edges.some(
        (e) => e.source === sourceId && e.target === targetId,
      );
      if (exists) return state;
      return {
        edges: [
          ...state.edges,
          {
            id: newEdgeId(sourceId, targetId),
            source: sourceId,
            target: targetId,
          },
        ],
      };
    });
  },

  renameNode: (nodeId, name) => {
    set((state) => ({
      nodes: state.nodes.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, name } } : n,
      ),
    }));
  },

  selectNode: (nodeId) => {
    set((state) => ({
      selectedNodeId: nodeId,
      nodes: state.nodes.map((n) => ({ ...n, selected: n.id === nodeId })),
    }));
  },

  loadDefinition: (definition) => {
    const nodes: CanvasNode[] = definition.nodes.map((n, index) =>
      makeNode(
        n.id,
        n.type as NodeType,
        n.position ?? { x: index * 240, y: 80 },
        n.name,
        n.params,
      ),
    );
    const edges: CanvasEdge[] = definition.edges.map((e) => ({
      id: e.id,
      source: e.source_node_id,
      target: e.target_node_id,
      ...(e.condition ? { label: e.condition } : {}),
    }));
    set({ nodes, edges, selectedNodeId: null, nodeSeq: nodes.length });
  },

  toDefinition: () => {
    const { nodes, edges } = get();
    const wireNodes: AgentNode[] = nodes.map((n) => ({
      id: n.id,
      name: n.data.name,
      type: n.data.type,
      params: n.data.params,
      position: { x: n.position.x, y: n.position.y },
    }));
    const wireEdges: AgentEdge[] = edges.map((e) => ({
      id: e.id,
      source_node_id: e.source,
      target_node_id: e.target,
      ...(typeof e.label === 'string' && e.label
        ? { condition: e.label }
        : {}),
    }));
    return { nodes: wireNodes, edges: wireEdges };
  },

  reset: () => {
    set({ nodes: [], edges: [], selectedNodeId: null, nodeSeq: 0 });
  },
}));
